/**
 * IQC auto-rules: SKU, supplier, or SKU+supplier sends matched lines to Holding after finish-container.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus } from "lucide-react";

import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import {
  createInboundIqcRule,
  fetchInboundIqcRuleOptions,
  fetchInboundIqcRules,
  setInboundIqcRuleActive,
  type InboundIqcRuleMatchType,
} from "./inboundApi";

export type InboundIqcRulesCardProps = {
  canEdit?: boolean;
};

export function InboundIqcRulesCard({ canEdit = true }: InboundIqcRulesCardProps): JSX.Element {
  const queryClient = useQueryClient();
  const rulesKey = ["shipping", "inbound", "iqc-rules"] as const;
  const rulesQuery = useQuery({ queryKey: rulesKey, queryFn: fetchInboundIqcRules });
  const optionsQuery = useQuery({
    queryKey: ["shipping", "inbound", "iqc-rule-options"],
    queryFn: fetchInboundIqcRuleOptions,
  });

  const [ruleType, setRuleType] = useState<InboundIqcRuleMatchType>("supplier");
  const [sku, setSku] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: rulesKey });
  const onError = (mutationError: unknown) =>
    setError(mutationError instanceof ApiError ? mutationError.message : String(mutationError));

  const createMutation = useMutation({
    mutationFn: () => {
      if (ruleType === "sku") {
        return createInboundIqcRule({ match_type: "sku", sku: sku.trim() });
      }
      if (ruleType === "supplier") {
        return createInboundIqcRule({ match_type: "supplier", supplier_id: Number(supplierId) });
      }
      return createInboundIqcRule({
        match_type: "sku_supplier",
        sku: sku.trim(),
        supplier_id: Number(supplierId),
      });
    },
    onSuccess: () => {
      setSku("");
      setSupplierId("");
      setSearch("");
      setError(null);
      invalidate();
    },
    onError,
  });

  const toggleMutation = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => setInboundIqcRuleActive(input.id, input.active),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError,
  });

  const supplierOptions = useMemo(() => {
    const list = optionsQuery.data?.suppliers ?? [];
    const needle = search.trim().toLowerCase();
    return list
      .filter(
        (option) =>
          needle === "" || (option.name ?? "").toLowerCase().includes(needle) || option.id.includes(needle),
      )
      .slice(0, 50)
      .map((option) => ({ value: option.id, label: option.name ?? `#${option.id}` }));
  }, [optionsQuery.data, search]);

  const setType = (next: InboundIqcRuleMatchType) => {
    setRuleType(next);
    setSku("");
    setSupplierId("");
    setSearch("");
  };

  const canCreate =
    canEdit &&
    (ruleType === "sku"
      ? sku.trim() !== ""
      : ruleType === "supplier"
        ? supplierId !== ""
        : sku.trim() !== "" && supplierId !== "");

  const rules = rulesQuery.data ?? [];

  return (
    <HubSettingsSection
      sectionId="inbound-iqc-rules"
      title="IQC rules"
      hoverHint="Matched lines go to Holding for inspection and IQC after the arrival container is finished."
    >
      {canEdit ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label>Match on</Label>
            <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
              {(["sku", "supplier", "sku_supplier"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setType(type)}
                  className={cn(
                    "h-9 px-3 text-sm font-medium",
                    ruleType === type ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50",
                  )}
                >
                  {type === "sku" ? "SKU" : type === "supplier" ? "Supplier" : "SKU + supplier"}
                </button>
              ))}
            </div>
          </div>
          {(ruleType === "sku" || ruleType === "sku_supplier") && (
            <div className="min-w-[10rem] space-y-1">
              <Label htmlFor="iqc-rule-sku">SKU</Label>
              <Input id="iqc-rule-sku" value={sku} onChange={(event) => setSku(event.target.value)} />
            </div>
          )}
          {(ruleType === "supplier" || ruleType === "sku_supplier") && (
            <div className="min-w-[14rem] flex-1 space-y-1">
              <Label>Supplier</Label>
              <SearchableSelect
                value={supplierId}
                onValueChange={(option) => setSupplierId(option?.value ?? "")}
                options={supplierOptions}
                searchValue={search}
                onSearchChange={setSearch}
                isLoading={optionsQuery.isLoading}
                placeholder="Pick a supplier…"
                emptyMessage="No matches"
              />
            </div>
          )}
          <Button
            disabled={!canCreate || createMutation.isPending}
            onClick={() => createMutation.mutate()}
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            Add rule
          </Button>
        </div>
      ) : (
        <p className="text-sm text-slate-600">Shipping module administrators can add and edit IQC rules.</p>
      )}

      {error ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </p>
      ) : null}

      {rulesQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading IQC rules…</p>
      ) : rules.length === 0 ? (
        <p className="text-sm text-slate-500">No IQC rules yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rules.map((rule) => (
            <li key={rule.id} className="flex flex-wrap items-center gap-2 py-2">
              <Badge tone="neutral">
                {rule.match_type === "sku"
                  ? "SKU"
                  : rule.match_type === "supplier"
                    ? "Supplier"
                    : "SKU + supplier"}
              </Badge>
              <span className="text-sm font-medium text-slate-900">
                {rule.match_type === "supplier"
                  ? rule.supplier_name ?? `#${rule.supplier_id ?? "—"}`
                  : rule.match_type === "sku"
                    ? rule.sku ?? "—"
                    : `${rule.sku ?? "—"} · ${rule.supplier_name ?? `#${rule.supplier_id ?? "—"}`}`}
              </span>
              <Badge tone={rule.active ? "success" : "muted"}>{rule.active ? "active" : "inactive"}</Badge>
              {canEdit ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={toggleMutation.isPending}
                  onClick={() => toggleMutation.mutate({ id: rule.id, active: !rule.active })}
                >
                  {rule.active ? "Deactivate" : "Reactivate"}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </HubSettingsSection>
  );
}
